import { useState, useEffect, useContext, useRef } from 'react';
import {
  Dialog, Backdrop, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, CircularProgress, Typography,
  Box, IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import { BlobServiceClient, BlobClient } from '@azure/storage-blob';
import { nanoid } from 'nanoid';
import { ModeContext } from './ModeContext';
import { SendToCosmos, LoadToCosmos } from './SendToCosmos';

const FileDetailModalFaq = ({ open, onClose, faqData, type, onUpdated }) => {
  const { loginInfo } = useContext(ModeContext);
  const isEditMode = type === 'edit' && faqData;
  const [isLoading, setIsLoading] = useState(false);

  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [answer, setAnswer] = useState('');
  const [filter2, setFilter2] = useState('ALL_USER');
  const [publicValue, setPublicValue] = useState('publish');
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState('');
  // 複数画像対応
  const [imageFiles, setImageFiles] = useState([]);           // 新規選択した画像ファイル配列
  const [imagePreviews, setImagePreviews] = useState([]);     // プレビュー用URL配列
  const [existingImageUrls, setExistingImageUrls] = useState([]); // 既存の画像URL配列
  const [removedImageUrls, setRemovedImageUrls] = useState([]); // 削除予定の画像URL配列
  const fileInputRef = useRef(null);

  // 既存データからimages配列を復元するヘルパー（後方互換対応）
  const parseImages = (doc) => {
    // URLからSASトークンを除去するヘルパー
    const cleanUrl = (url) => url ? url.split('?')[0] : '';
    const cleanUrls = (urls) => urls.filter(Boolean).map(cleanUrl).filter(Boolean);

    // 新形式: images配列フィールド
    if (doc?.images && Array.isArray(doc.images)) {
      return cleanUrls(doc.images);
    }
    // rawプロパティ内にimagesがある場合（FilelistDisplayから渡されるデータ構造）
    if (doc?.raw?.images && Array.isArray(doc.raw.images)) {
      return cleanUrls(doc.raw.images);
    }
    // 旧形式: imageUrl（JSON配列文字列 or 単一URL）
    const value = doc?.imageUrl || doc?.raw?.imageUrl;
    if (!value) return [];
    if (Array.isArray(value)) return cleanUrls(value);
    if (typeof value === 'string' && value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return cleanUrls(parsed);
      } catch { /* パース失敗時は単一URLとして扱う */ }
    }
    return value ? [cleanUrl(value)] : [];
  };

  useEffect(() => {
    if (isEditMode) {
      const loadFileData = async () => {
        try {
          const res = await LoadToCosmos({ id: faqData.id, container: 'con-concierge-file' });
          const fileDoc = res?.item?.[0] || res?.item;
          if (fileDoc) {
            setCategory(fileDoc.category || faqData.category || '');
            setTitle(fileDoc.filename || faqData.title || '');
            setAnswer(fileDoc.filter3 || faqData.content || faqData.answer || '');
            setFilter2(fileDoc.filter2 || faqData.range || faqData.filter2 || 'ALL_USER');
            setPublicValue(fileDoc.status || faqData.status || 'publish');
          } else {
            setCategory(faqData.category || '');
            setTitle(faqData.title || '');
            setAnswer(faqData.content || faqData.answer || '');
            setFilter2(faqData.range || faqData.filter2 || 'ALL_USER');
            setPublicValue(faqData.status || 'publish');
          }
          const updatedBy = fileDoc?.filter4 || faqData.display_name || '';
          const updatedAt = fileDoc?.timestamp
            ? new Date(fileDoc.timestamp).toLocaleString('ja-JP')
            : faqData.update_at || '';
          setLastUpdatedBy(updatedBy);
          setLastUpdatedAt(updatedAt);
          // 既存の画像URLを配列として復元（fileDoc優先、なければfaqDataから）
          const urls = parseImages(fileDoc) .length > 0
            ? parseImages(fileDoc)
            : parseImages(faqData);
          setExistingImageUrls(urls);
          setImageFiles([]);
          setImagePreviews([]);
          setRemovedImageUrls([]);
        } catch (err) {
          console.error('con-concierge-file取得エラー:', err);
          setCategory(faqData.category || '');
          setTitle(faqData.title || '');
          setAnswer(faqData.content || faqData.answer || '');
          setFilter2(faqData.range || faqData.filter2 || 'ALL_USER');
          setPublicValue(faqData.status || 'publish');
          setLastUpdatedBy(faqData.display_name || '');
          setLastUpdatedAt(faqData.update_at || '');
          setExistingImageUrls(parseImages(faqData));
          setImageFiles([]);
          setImagePreviews([]);
          setRemovedImageUrls([]);
        }
      };
      loadFileData();
    } else {
      setCategory(faqData?.category || '');
      setTitle('');
      setAnswer('');
      setFilter2('ALL_USER');
      setPublicValue('publish');
      setImageFiles([]);
      setImagePreviews([]);
      setExistingImageUrls([]);
      setRemovedImageUrls([]);
    }
  }, [type, faqData]);

  const generateId = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = 'user-';
    for (let i = 0; i < 7; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const formatNow = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // 画像ファイル選択ハンドラー（複数対応）
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    const validPreviews = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        alert(`「${file.name}」は画像ファイルではありません。スキップします。`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert(`「${file.name}」は5MBを超えています。スキップします。`);
        continue;
      }
      validFiles.push(file);
      validPreviews.push(URL.createObjectURL(file));
    }
    if (validFiles.length > 0) {
      setImageFiles((prev) => [...prev, ...validFiles]);
      setImagePreviews((prev) => [...prev, ...validPreviews]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 新規追加画像の個別削除
  const handleRemoveNewImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== index);
    });
  };

  // 既存画像の個別削除（保存時にBlobも削除するため追跡）
  const handleRemoveExistingImage = (index) => {
    const removedUrl = existingImageUrls[index];
    if (removedUrl) {
      setRemovedImageUrls((prev) => [...prev, removedUrl]);
    }
    setExistingImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  // Blob Storageから画像を削除
  const deleteImageFromBlob = async (imageUrl) => {
    try {
      // SASトークンが付いている場合は除去してベースURLを取得
      const cleanUrl = imageUrl.split('?')[0];
      const blob3Base = process.env.REACT_APP_BLOB3_URL;
      if (!cleanUrl.includes(new URL(blob3Base).hostname)) return;
      const blobPath = new URL(cleanUrl).pathname.split('/').slice(2).join('/');
      const deleteSasUrl = `${blob3Base}/${blobPath}?${process.env.REACT_APP_BLOB3_TOKEN}`;
      const blobClient = new BlobClient(deleteSasUrl);
      await blobClient.delete();
    } catch (err) {
      console.error('Blob画像削除エラー:', err);
    }
  };

  // 画像をBlob Storageにアップロード（SASトークンなしのURLを返す）
  const uploadImageToBlob = async (file, docId) => {
    const containerSasUrl = `${process.env.REACT_APP_BLOB3_URL}?${process.env.REACT_APP_BLOB3_TOKEN}`;
    const containerClient = new BlobServiceClient(containerSasUrl).getContainerClient('');
    const uniqueId = nanoid();
    const ext = file.name.split('.').pop();
    const blobName = `faq/${docId}/${uniqueId}.${ext}`;
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);
    await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type },
    });
    // SASトークンを除去したクリーンなURLを返す
    return blockBlobClient.url.split('?')[0];
  };

  // 画像URLにSASトークンを付与して表示用URLを生成
  const getImageDisplayUrl = (url) => {
    if (!url) return '';
    // すでにSASトークンが付いている場合はそのまま返す
    if (url.includes('?')) return url;
    return `${url}?${process.env.REACT_APP_BLOB3_TOKEN}`;
  };

  const handleSave = async () => {
    if (!category || !answer) {
      alert('カテゴリーと内容は必須です。');
      return;
    }
    const confirm = window.confirm('保存しますか？');
    if (!confirm) return;

    setIsLoading(true);
    try {
      const docId = isEditMode ? faqData.id : generateId();

      // 新規画像をすべてアップロード
      const newUploadedUrls = [];
      for (const file of imageFiles) {
        const url = await uploadImageToBlob(file, docId);
        newUploadedUrls.push(url);
      }

      // 既存画像URL + 新規アップロードURLを結合
      const allImageUrls = [...existingImageUrls, ...newUploadedUrls];

      // 画像URLからimgタグを生成してtextフィールドに格納（表示用にSASトークン付き）
      const imageText = allImageUrls
        .map((url) => `<img src="${getImageDisplayUrl(url)}" alt="${title || 'FAQ画像'}">`)
        .join('\n');

      const payload = {
        id: docId,
        tenant_id: docId,
        user: loginInfo?.userId || '',
        type: 'user-faq',
        category: category,
        sub_category: '',
        group: 'ServicePortal',
        range: filter2,
        display_name: loginInfo?.userName || '',
        title: title,
        content: answer,
        text: imageText,
        answer: '',
        site_name: '',
        URL: '',
        images: allImageUrls,
        status: publicValue,
        tag: loginInfo?.compId || '',
        update_at: formatNow(),
        container: 'conversations02',
      };
      await SendToCosmos(payload);

      // con-concierge-fileにも保存
      const filePayload = {
        id: docId,
        fileid: docId,
        bu: loginInfo?.compId || '',
        category: category,
        filename: title,
        url: '',
        filter1: category,
        filter2: filter2,
        filter3: answer,
        filter4: loginInfo?.userId || '',
        filter5: 'u_faq',
        images: allImageUrls,
        status: publicValue,
        timestamp: Date.now(),
        container: 'con-concierge-file',
      };
      await SendToCosmos(filePayload);

      // 削除された既存画像のBlobを削除
      for (const url of removedImageUrls) {
        await deleteImageFromBlob(url);
      }

      if (onUpdated) await onUpdated();
      alert('保存しました。');
      onClose();
    } catch (error) {
      console.error('FAQ保存エラー:', error);
      alert('保存に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode) return;
    const confirm = window.confirm('このFAQを削除しますか？');
    if (!confirm) return;

    setIsLoading(true);
    try {
      // 記事に紐づく全画像のBlobを削除
      const allImages = [...existingImageUrls, ...removedImageUrls];
      for (const url of allImages) {
        await deleteImageFromBlob(url);
      }

      const payload = {
        ...faqData,
        status: 'delete',
        update_at: formatNow(),
        container: 'conversations02',
      };
      await SendToCosmos(payload);

      const faqContent = faqData.content || faqData.answer || '';
      const filePayload = {
        id: faqData.id,
        fileid: faqData.id,
        bu: loginInfo?.compId || '',
        category: faqData.category || '',
        filename: faqData.title || '',
        url: '',
        filter1: faqData.category || '',
        filter2: faqData.range || faqData.filter2 || 'ALL_USER',
        filter3: faqContent,
        filter4: '',
        filter5: 'u_faq',
        status: 'delete',
        timestamp: Date.now(),
        container: 'con-concierge-file',
      };
      await SendToCosmos(filePayload);
      if (onUpdated) await onUpdated();
      alert('削除しました。');
      onClose();
    } catch (error) {
      console.error('FAQ削除エラー:', error);
      alert('削除に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" disableEscapeKeyDown={isLoading}>
      <DialogTitle>{isEditMode ? 'FAQ編集' : 'FAQ登録'}</DialogTitle>
      <DialogContent dividers>
        {isEditMode && (lastUpdatedBy || lastUpdatedAt) && (
          <Typography variant="body2" sx={{ mb: 1, color: '#888', fontSize: '0.8rem' }}>
            最終更新者: {lastUpdatedBy || '不明'}　／　更新日時: {lastUpdatedAt || '不明'}
          </Typography>
        )}
        <TextField
          variant="outlined" fullWidth label="カテゴリー" margin="normal"
          value={category} onChange={(e) => setCategory(e.target.value)}
        />
        <TextField
          variant="outlined" fullWidth label="タイトル" margin="normal"
          placeholder="FAQのタイトルを入力してください。"
          value={title} onChange={(e) => setTitle(e.target.value)}
        />
        <TextField
          variant="outlined" fullWidth label="内容" margin="normal"
          multiline rows={6} placeholder="FAQの内容を入力してください。"
          value={answer} onChange={(e) => setAnswer(e.target.value)}
        />
        <TextField
          variant="outlined" fullWidth label="閲覧対象" margin="normal"
          value={filter2} onChange={(e) => setFilter2(e.target.value)} select
        >
          {(loginInfo.authorityId === 'HQ' || loginInfo.authorityId === 'store') ? [
            <MenuItem key="ALL_USER" value="ALL_USER">全従業員</MenuItem>,
            <MenuItem key="HQ_ONLY" value="HQ_ONLY">本部のみ</MenuItem>,
            <MenuItem key="STORE_ONLY" value="STORE_ONLY">店舗のみ</MenuItem>
          ] : [
            <MenuItem key="MY_ONLY" value="MY_ONLY">自分のみ</MenuItem>,
            <MenuItem key="ALL_USER" value="ALL_USER">グループ全員</MenuItem>
          ]}
        </TextField>

        {/* 画像アップロードエリア（複数対応） */}
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>画像（任意・複数選択可）</Typography>
          {/* 既存画像のプレビュー */}
          {existingImageUrls.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {existingImageUrls.map((url, index) => (
                <Box key={`existing-${index}`} sx={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={getImageDisplayUrl(url)}
                    alt={`保存済み画像${index + 1}`}
                    style={{
                      maxWidth: 180,
                      maxHeight: 140,
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      objectFit: 'contain',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveExistingImage(index)}
                    sx={{
                      position: 'absolute', top: -8, right: -8,
                      bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                    }}
                    aria-label="画像を削除"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          {/* 新規追加画像のプレビュー */}
          {imagePreviews.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {imagePreviews.map((preview, index) => (
                <Box key={`new-${index}`} sx={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={preview}
                    alt={`新規画像${index + 1}`}
                    style={{
                      maxWidth: 180,
                      maxHeight: 140,
                      borderRadius: 4,
                      border: '1px solid #4caf50',
                      objectFit: 'contain',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveNewImage(index)}
                    sx={{
                      position: 'absolute', top: -8, right: -8,
                      bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                    }}
                    aria-label="画像を削除"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          {/* ファイル選択ボタン */}
          <Button
            variant="outlined"
            startIcon={<ImageIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ textTransform: 'none' }}
          >
            画像を選択
          </Button>
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
            ※ 対応形式: JPG, PNG, GIF（各5MB以下・複数選択可）
          </Typography>
        </Box>

        <TextField
          variant="outlined" fullWidth label="公開設定" margin="normal"
          value={publicValue} onChange={(e) => setPublicValue(e.target.value)} select
        >
          <MenuItem value="publish">公開</MenuItem>
          <MenuItem value="private">非公開</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        {isEditMode && (
          <Button onClick={handleDelete} disabled={isLoading}>削除</Button>
        )}
        <Button onClick={handleSave} color="primary" disabled={isLoading}>保存</Button>
        <Button onClick={onClose} color="secondary" disabled={isLoading}>キャンセル</Button>
      </DialogActions>
      <Backdrop open={open && isLoading} sx={{ color: '#fff', zIndex: 2000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default FileDetailModalFaq;
